import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  GroupPopulated,
  CreateGroupDto,
  UpdateGroupDto,
  AddGroupMemberDto,
  GroupQueryParams,
  GroupResponse,
  GroupListResponse,
  CreateGroupResponse,
  UpdateGroupResponse,
  DeleteGroupResponse,
  AddGroupMemberResponse,
  RemoveGroupMemberResponse
} from '../models';

/**
 * Service for managing groups via the User Management API
 * 
 * This service provides comprehensive methods for:
 * - CRUD operations on groups
 * - Group membership management
 * - Organization-scoped group operations
 * 
 * @remarks
 * **Authentication:** All endpoints require authentication via Bearer token.
 * Authentication is handled by HTTP interceptors provided by the consuming application.
 * 
 * **Access Control:**
 * - Users can only access groups in organizations they belong to
 * - Group creators can manage their groups
 * - Organization admins have full access to all groups in the organization
 * 
 * **Business Rules:**
 * - Group names must be unique within an organization
 * - Group creator is automatically added as the first member
 * - Members must belong to the organization
 * 
 * @example
 * ```typescript
 * constructor(private groupService: GroupService) {}
 * 
 * // Create a group
 * this.groupService.createGroup({
 *   name: 'Developers',
 *   organizationId: '507f1f77bcf86cd799439011',
 *   description: 'Development team members'
 * }).subscribe(response => {
 *   console.log('Group created:', response.data);
 * });
 * 
 * // Add member to group
 * this.groupService.addMember('groupId', { userId: 'userId' })
 *   .subscribe(response => {
 *     console.log('Member added:', response.data);
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class GroupService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/groups`;
    this.logDebug('GroupService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // CREATE OPERATIONS
  // ============================================================================

  /**
   * Create a new group
   * 
   * @param dto - Group creation data
   * @returns Observable of API response with created group
   * 
   * @throws {Error} 400 - Invalid request data
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (not a member of the organization)
   * @throws {Error} 409 - Conflict (group name already exists in organization)
   * 
   * @remarks
   * The authenticated user is automatically added as the first member.
   * User must be a member of the specified organization.
   * 
   * @example
   * ```typescript
   * this.groupService.createGroup({
   *   name: 'Marketing Team',
   *   organizationId: '507f1f77bcf86cd799439011',
   *   description: 'Marketing department members'
   * }).subscribe({
   *   next: response => console.log('Created:', response.data),
   *   error: err => console.error('Failed:', err)
   * });
   * ```
   */
  createGroup(dto: CreateGroupDto): Observable<CreateGroupResponse> {
    this.logDebug('Creating group', { name: dto.name });

    return this.http.post<CreateGroupResponse>(this.apiUrl, dto).pipe(
      map(response => {
        this.logDebug('Group created', { groupId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('createGroup', error))
    );
  }

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Get all groups with optional organization filtering
   * 
   * @param params - Query parameters
   * @returns Observable of API response with groups array
   * 
   * @throws {Error} 401 - Unauthorized
   * 
   * @remarks
   * Without organizationId: Returns groups from all user's organizations
   * With organizationId: Returns groups from specified organization only
   * 
   * @example
   * ```typescript
   * // Get all groups
   * this.groupService.getAllGroups().subscribe(response => {
   *   console.log('Groups:', response.data);
   * });
   * 
   * // Get groups for specific organization
   * this.groupService.getAllGroups({ 
   *   organizationId: '507f1f77bcf86cd799439011' 
   * }).subscribe(response => {
   *   console.log('Org groups:', response.data);
   * });
   * ```
   */
  getAllGroups(params?: GroupQueryParams): Observable<GroupListResponse> {
    this.logDebug('Getting all groups', params);

    let httpParams = new HttpParams();
    if (params?.organizationId) {
      httpParams = httpParams.set('organizationId', params.organizationId);
    }

    return this.http.get<GroupListResponse>(this.apiUrl, { params: httpParams }).pipe(
      map(response => {
        this.logDebug('Groups retrieved', { count: response.count });
        return response;
      }),
      catchError(error => this.handleError('getAllGroups', error))
    );
  }

  /**
   * Get a single group by ID
   * 
   * @param id - Group ID
   * @returns Observable of API response with group data
   * 
   * @throws {Error} 400 - Invalid group ID
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (not a member of the organization)
   * @throws {Error} 404 - Group not found
   * 
   * @example
   * ```typescript
   * this.groupService.getGroupById('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log('Group:', response.data.name);
   *     console.log('Members:', response.data.members);
   *   });
   * ```
   */
  getGroupById(id: string): Observable<GroupResponse> {
    this.logDebug('Getting group by ID', { id });

    return this.http.get<GroupResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        this.logDebug('Group retrieved', { groupId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('getGroupById', error))
    );
  }

  // ============================================================================
  // UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update a group
   * 
   * @param id - Group ID
   * @param dto - Update data
   * @returns Observable of API response with updated group
   * 
   * @throws {Error} 400 - Invalid data
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden
   * @throws {Error} 404 - Group not found
   * @throws {Error} 409 - Conflict (name already exists)
   * 
   * @example
   * ```typescript
   * this.groupService.updateGroup('groupId', {
   *   name: 'New Team Name',
   *   description: 'Updated description'
   * }).subscribe(response => {
   *   console.log('Updated:', response.data);
   * });
   * ```
   */
  updateGroup(id: string, dto: UpdateGroupDto): Observable<UpdateGroupResponse> {
    this.logDebug('Updating group', { id, updates: Object.keys(dto) });

    return this.http.put<UpdateGroupResponse>(`${this.apiUrl}/${id}`, dto).pipe(
      map(response => {
        this.logDebug('Group updated', { groupId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('updateGroup', error))
    );
  }

  // ============================================================================
  // DELETE OPERATIONS
  // ============================================================================

  /**
   * Delete a group
   * 
   * @param id - Group ID
   * @returns Observable of API response
   * 
   * @throws {Error} 400 - Invalid group ID
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden
   * @throws {Error} 404 - Group not found
   * 
   * @example
   * ```typescript
   * this.groupService.deleteGroup('groupId').subscribe({
   *   next: response => console.log(response.message),
   *   error: err => console.error('Delete failed:', err)
   * });
   * ```
   */
  deleteGroup(id: string): Observable<DeleteGroupResponse> {
    this.logDebug('Deleting group', { id });

    return this.http.delete<DeleteGroupResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        this.logDebug('Group deleted', { id });
        return response;
      }),
      catchError(error => this.handleError('deleteGroup', error))
    );
  }

  // ============================================================================
  // MEMBER MANAGEMENT
  // ============================================================================

  /**
   * Add a member to a group
   * 
   * @param groupId - Group ID
   * @param dto - Member data with userId
   * @returns Observable of API response with updated group
   * 
   * @throws {Error} 400 - Invalid data or user not in organization
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden
   * @throws {Error} 404 - Group or user not found
   * @throws {Error} 409 - User already a member
   * 
   * @remarks
   * User must be a member of the group's organization.
   * 
   * @example
   * ```typescript
   * this.groupService.addMember('groupId', { 
   *   userId: '507f1f77bcf86cd799439011' 
   * }).subscribe({
   *   next: response => console.log('Member added:', response.data),
   *   error: err => console.error('Failed:', err)
   * });
   * ```
   */
  addMember(groupId: string, dto: AddGroupMemberDto): Observable<AddGroupMemberResponse> {
    this.logDebug('Adding member to group', { groupId, userId: dto.userId });

    return this.http.post<AddGroupMemberResponse>(
      `${this.apiUrl}/${groupId}/members`,
      dto
    ).pipe(
      map(response => {
        this.logDebug('Member added', { groupId, userId: dto.userId });
        return response;
      }),
      catchError(error => this.handleError('addMember', error))
    );
  }

  /**
   * Remove a member from a group
   * 
   * @param groupId - Group ID
   * @param userId - User ID to remove
   * @returns Observable of API response with updated group
   * 
   * @throws {Error} 400 - Invalid data
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden
   * @throws {Error} 404 - Group or user not found
   * 
   * @example
   * ```typescript
   * this.groupService.removeMember('groupId', 'userId')
   *   .subscribe({
   *     next: response => console.log('Member removed:', response.data),
   *     error: err => console.error('Failed:', err)
   *   });
   * ```
   */
  removeMember(groupId: string, userId: string): Observable<RemoveGroupMemberResponse> {
    this.logDebug('Removing member from group', { groupId, userId });

    return this.http.delete<RemoveGroupMemberResponse>(
      `${this.apiUrl}/${groupId}/members/${userId}`
    ).pipe(
      map(response => {
        this.logDebug('Member removed', { groupId, userId });
        return response;
      }),
      catchError(error => this.handleError('removeMember', error))
    );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get groups data without API wrapper
   * 
   * @param params - Query parameters
   * @returns Observable of groups array
   */
  getGroupsData(params?: GroupQueryParams): Observable<GroupPopulated[]> {
    return this.getAllGroups(params).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get group data without API wrapper
   * 
   * @param id - Group ID
   * @returns Observable of group
   */
  getGroupData(id: string): Observable<GroupPopulated> {
    return this.getGroupById(id).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get groups count
   * 
   * @param params - Query parameters
   * @returns Observable of count
   */
  getGroupsCount(params?: GroupQueryParams): Observable<number> {
    return this.getAllGroups(params).pipe(
      map(response => response.count || 0)
    );
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Handle HTTP errors
   */
  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Log debug information
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[GroupService] ${message}`, data || '');
    }
  }
}
